import React, { useCallback, useState } from 'react';
import { Modal, Button, Alert } from 'antd';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FloatingLabelSearchSelect } from '../elements/floatingLabelInput/FloatingLabelInput';
import { fetchMedics } from '../../api/medics-api';
import { setReferredByDoctor } from '../../api/user-api';
import { useInfiniteOptions, withSelectedOption } from '../../hooks/useInfiniteOptions';

// Unlike DoctorReferralSection/AgentMentorLinkSection, this is not set-once — an
// admin can pick a different doctor even if the client already has one, so the
// picker is always shown (no locked read-only state).
const SetReferredByDoctorModal = ({ isOpen, onClose, onSuccess, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const currentMentor = user?.referredByDoctor;

  const fetchDoctorPage = useCallback(async (page, limit, search) => {
    const { data, success } = await fetchMedics({ page, limit, search: search || null });
    if (!success) return null;
    return { items: data?.doctors || [], totalPages: data?.pagination?.totalPages };
  }, []);
  const {
    items: loadedDoctors,
    loading: doctorsLoading,
    onSearch: setDoctorSearch,
    onPopupScroll: onDoctorsScroll,
  } = useInfiniteOptions(fetchDoctorPage, { enabled: isOpen, reloadKey: isOpen });
  const doctors = withSelectedOption(loadedDoctors, selectedDoctor, '_id');

  const resetForm = () => {
    setError('');
    setSelectedDoctor(null);
    setDoctorSearch('');
  };

  const handleSelectDoctor = (val) => {
    const doctorMatch = doctors.find((d) => d._id === val) || null;
    setSelectedDoctor(doctorMatch);
    setError('');
  };

  const handleOk = async () => {
    if (!selectedDoctor) {
      setError('Izaberite mentora.');
      return;
    }

    setLoading(true);
    setError('');
    toast.loading('Postavljanje mentorske preporuke...');

    const { success, message } = await setReferredByDoctor(
      user._id,
      selectedDoctor._id
    );

    setLoading(false);

    if (!success) {
      toast.dismiss();
      toast.error(message || 'Došlo je do greške pri postavljanju mentorske preporuke.');
      setError(message || 'Došlo je do greške pri postavljanju mentorske preporuke.');
      return;
    }

    toast.dismiss();
    toast.success('Mentorska preporuka je uspešno postavljena.');
    onSuccess();
    onClose();
    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const doctorOptions = doctors.map((d) => ({
    value: d._id,
    label: `${d.firstName} ${d.lastName} — ${d.referralCode || '-'}`,
  }));

  return (
    <Modal
      title="Postavi mentorsku preporuku"
      open={isOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={handleOk}
          className="btn-primary"
          disabled={loading}
        >
          {loading ? 'Čuvanje...' : 'Sačuvaj'}
        </Button>,
      ]}
    >
      <p>
        Korisnik: <strong>{user?.firstName} {user?.lastName}</strong>
      </p>
      <p style={{ marginBottom: 16 }}>
        {currentMentor
          ? <>Trenutni mentor: <strong>{currentMentor.firstName} {currentMentor.lastName}</strong> ({currentMentor.referralCode || '-'})</>
          : 'Korisnik trenutno nema mentora.'}
      </p>

      <FloatingLabelSearchSelect
        label="Izaberite mentora"
        placeholder="Pretraži po imenu ili referral kodu..."
        value={selectedDoctor?._id}
        onChange={handleSelectDoctor}
        onSearch={setDoctorSearch}
        loading={doctorsLoading}
        onPopupScroll={onDoctorsScroll}
        options={doctorOptions}
      />

      <p style={{ fontSize: 12, color: '#667085', marginTop: 12 }}>
        Ova izmena utiče samo na buduće porudžbine i proviziju — ne menja
        prethodno kreirane porudžbine.
      </p>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

SetReferredByDoctorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  user: PropTypes.shape({
    _id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    referredByDoctor: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      referralCode: PropTypes.string,
    }),
  }),
};

export default SetReferredByDoctorModal;
